import { useState, useEffect } from 'react';
import './style.scoped.css';
import Toolbar from 'src/components/Toolbar';
import Button from 'src/components/Button';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { confirmQuit, formatMoney, goBackCustom, showError } from 'src/utils';
import { createOrderPeriodic } from 'src/api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import LoadingService from 'src/utils/LoadingService';
import InfoIcon from 'src/assets/info_icon.svg';
import paymentSt, { PaymentStore } from 'src/stores/PaymentStore';
import { observer } from 'mobx-react-lite';
import Checkbox from 'src/components/Checkbox';
import PolicyCard from 'src/components/PolicyCard';
dayjs.extend(customParseFormat);

const CARD_DETAILS = ['payFrequency', 'periodicPremium', 'agentName', 'agentPhone', 'agentCode'];

interface PeriodicPaymentObserverProps {
  paymentStore?: PaymentStore;
}
const PeriodicPaymentObserver = observer(({ paymentStore } : PeriodicPaymentObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const _handlePayment = async () => {
    if(paymentStore?.periodicPaymentInfo?.amount == 0){
      showError(t('msg_13'));
    }else{
      let request = {
        requestId: paymentStore?.periodicPaymentInfo?.requestId,
        amount: paymentStore?.periodicPaymentInfo?.amount,
        policyNumber: paymentStore?.periodicPaymentInfo?.policyNum,
        duePremium: paymentStore?.periodicPaymentInfo?.duePremium
      }
      try {
        LoadingService.instance.current.open();
        let createOrderRes = await createOrderPeriodic(request);
        LoadingService.instance.current.close();
        if (window?.ReactNativeWebView) {
          window?.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'PAYMENT_HUB_TRANSACTION',
              data: createOrderRes?.data,
            })
          );
        }
      } catch (error) {
        LoadingService.instance.current.close();
        showError(error?.response?.data?.error_message);
      }
    }
  };

  const _handleClose = () => {
    confirmQuit(history, InfoIcon);
  };

  const _handleBack = () => {
    goBackCustom(history);
  };

  const _handleChangePayPeriodic = (data) => {
    let amount = data.total - Number(paymentStore?.periodicPaymentInfo?.suspenseAmt);
    let check = data.total > 0 && amount > 0;
    paymentStore.updatePeriodicPaymentInfo({
      amount: check ? amount : 0
    });
  };

  return (
    <div className="screen-container bg-background view-periodic-payment">
      <Toolbar
        title={t('periodic_payment')}
        showClose={true}
        onClose={_handleClose}
        onBack={_handleBack}
      />
      <div className="max-w-md mx-auto screenScrollContainer">
        <div className={"px-4 mt-4 pt-4 bg-surface"}>
          <PolicyCard 
            policy={paymentStore?.periodicPaymentInfo ?? {}}
            showArrowIcon= {false}
            initShowDetail= {paymentStore?.periodicPaymentInfo?.showDetail ?? false}
            showBorder={paymentStore?.periodicPaymentInfo?.showDetail ?? false}
            details={CARD_DETAILS}
          />
        </div>
        
        <div className="bg-surface mt-4 px-4">
            <div className="flex flex-row pt-4 pb-4 grid grid-cols-2 border border-line-bottom">
              <div className="flex-1 bodyText2 text-onSurfaceVariant">
                {t('excess_payment')}
              </div>
              <div className="smallTitle2 text-right">
                {`${formatMoney(paymentStore?.periodicPaymentInfo?.suspenseAmt)} ${paymentStore?.periodicPaymentInfo?.currency ?? ""}`}
              </div>
            </div>
        </div>

        <div className='bg-surface px-4 pb-4 pt-4'>
          <div className="text-base font-bold text-onSurface">
            {t('periodic_1')}
          </div>
          <div className="bodyText2 text-onSurfaceVariant mt-2">{t('select_payment_fees')}</div>
        </div>

        <div className="px-4 pt-4 pb-4 mb-8">
          <div>
            <CheckBoxGroup list={paymentStore?.periodicPaymentInfo?.duePremium} currency={paymentStore?.periodicPaymentInfo?.currency} onChange={_handleChangePayPeriodic} />
          </div>
        </div>

        {/* <div className='bg-surface mb-8'>
          <Checkbox
            textComponent={
              <div>
                <div className="text-base font-bold text-onSurface">
                {t('additional_insurance_fee')}
                </div>
                <div className="bodyText2 text-onSurfaceVariant">{t('hint_additional_insurance_fee')}</div>
              </div>
            }
            checked={false}
            onCheck={() => {}}
            changeBackground={false}
            type="card"
          />
          <div className="px-4 pb-8 pt-4">
            <TextInput
              placeholder={t('topup_fee_hint')}
              value={topUpValue}
              onChange={_handleChange}
              type={'number'}
              helperText={`${t('min')} ${formatMoney(policy.topUpMinAmt)} ${policy.currency}; ${t('max')} ${formatMoney(policy.topUpMaxAmt)} ${policy.currency}`}
            />
            {!!errTopUpValue && (
              <div className={'helperTextError'}>{t('msg_09')}</div>
            )}
          </div>
        </div> */}
      </div>
      <div className="bottomButtonBlock">
        <div className='border border-line'>
          <div className="flex flex-row pt-1 pl-4 pr-4 pb-4 bg-surface">
            <div className="flex-1 smallTitle2">{t('total_fee')}</div>
            <div className="blockTitlePrimary">
              {formatMoney(paymentStore?.periodicPaymentInfo?.amount) + ` ${paymentStore?.periodicPaymentInfo?.currency ?? ""}`}
            </div>
        </div>
        </div>
        <div className='px-4 pt-3'>
          <Button
            text={t('payment')}
            onClick={_handlePayment}
            extraClassName={'w-full'}
            disabled={false}
          />
        </div>
      </div>
    </div>
  );
});

const CheckBoxGroup = (props) => {
  const { t } = useTranslation();
  const { list, onChange, currency } = props;
  const [checkedState, setCheckedState] = useState([]);

  useEffect(() => {
      if(list){
        let initCheckState = [];
        for(let item of list){
          initCheckState.push(item.checked);
        }
        setCheckedState(initCheckState);
      }
  }, [list]);

  const handleOnChange = (position) => {
    if(position == 0) return;
    
    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item
    );

    let listClone = [...list];
    for(let index in updatedCheckedState){
      listClone[index].checked = updatedCheckedState[index];
    }

    setCheckedState(updatedCheckedState);

    const total = updatedCheckedState.reduce(
      (sum, currentState, index) => {
        if (currentState === true) {
          return sum + Number(list[index].dueAmount);
        }
        return sum;
      },
      0
    );

    onChange({ list: listClone, total: total });
  };

  return <>
      {list?.map((item, index) => <div key={index} className={"b-radius-4 mb-4"}><Checkbox
          key={index}
          textComponent={
              <div className="flex flex-row grid grid-cols-2">
                  <div className="flex-1 bodyText2 text-onSurfaceVariant">
                  {item.dueFromDate ? dayjs(item.dueFromDate).format(t('date_format1')) : ''}
                  </div>
                  <div className="smallTitle2 text-right">
                  {`${formatMoney(item.dueAmount)} ${currency ?? ""}`}
                  </div>
              </div>
          }
          checked={checkedState[index]}
          onCheck={() => handleOnChange(index)}
          type="card"
      /></div>)}
  </>
}

const PeriodicPayment = () => <PeriodicPaymentObserver paymentStore={paymentSt} />;
export default PeriodicPayment;
